import { gql, gqlFetch } from '@/service/gql'
import type { ResultOf } from 'gql.tada'
import type { SanitizedGql } from '@/util/gqlUtil'
import { trackFieldsFrag } from '@/track/trackFieldsFrag'
import { podcastEpisodeFieldsFrag } from '@/podcast/podcastEpisodeFieldsFrag'
import { getAuth } from '@/auth/authRef'

/**
 * Party-session GraphQL — create a Firefly queue, wrap it in a party
 * session, query session state, and append tracks to the queue.
 *
 * Notes:
 *   - `createQueue` needs a seed `playParams.id`. We pass whatever the
 *     caller hands us (typically a catalog track MRN); the queue can
 *     be appended via `addToQueue` later.
 *   - The session id (`shared-…`) is returned by `createPartySession`
 *     and is the value we encode in the QR code's URL.
 *   - These operations have no `@include(if: $isAuthenticated)` fields,
 *     so the variable would be unused and Firefly rejects the
 *     operation — keep the variable list tight.
 */

// language=GraphQL
const createPartySessionMutation = gql(`
  mutation CreatePartySession($queueId: String!) {
    createPartySession(queueId: $queueId) {
      id
      url
      host { id name }
      status
    }
  }
`)

export type CreatePartySessionResult = SanitizedGql<ResultOf<typeof createPartySessionMutation>>

export async function createPartySession(queueId: string): Promise<CreatePartySessionResult> {
    return gqlFetch(createPartySessionMutation, { queueId }, { isRetryableError: () => true })
}

// language=GraphQL
const endPartySessionMutation = gql(`
  mutation EndPartySession($id: String!) {
    endPartySession(id: $id) {
      id
      status
    }
  }
`)

export async function endPartySession(id: string): Promise<void> {
    await gqlFetch(endPartySessionMutation, { id }, { isRetryableError: () => false })
}

// language=GraphQL
const queueViewQuery = gql(
    `
  query QueueView($id: String!, $currentEntityReferenceId: String, $isAuthenticated: Boolean!) {
    queue(id: $id, currentEntityReferenceId: $currentEntityReferenceId) {
      id
      currentEntityReferenceId
      view {
        edges {
          entityReferenceId
          node {
            ... on Track {
              ...trackFields
            }
            ... on PodcastEpisode {
              ...podcastEpisodeFields
            }
          }
        }
      }
    }
  }
`,
    [trackFieldsFrag, podcastEpisodeFieldsFrag]
)

// language=GraphQL
//
// Hermes subscription — fires whenever the host's CloudQueue
// changes (guests add or remove tracks). The push payload is tiny
// (just the queue id) — the controller re-fetches the queue view
// via the http path on each event so the rest of the codebase keeps
// using the same queue-state model.
export const queueUpdatedSubscription = gql(`
  subscription QueueUpdated($queueId: String!) {
    queueUpdated(queueId: $queueId) {
      id
    }
  }
`)

// language=GraphQL
const removeFromQueueMutation = gql(`
  mutation RemoveFromQueue($id: String!, $entityReferenceIds: [String!]) {
    removeFromQueue(id: $id, entityReferenceIds: $entityReferenceIds) {
      status
    }
  }
`)

export async function removeFromCloudQueue(
    queueId: string,
    entityReferenceIds: string[]
): Promise<void> {
    await gqlFetch(
        removeFromQueueMutation,
        { id: queueId, entityReferenceIds },
        { isRetryableError: () => false }
    )
}

export type QueueUpdatedResult = SanitizedGql<ResultOf<typeof queueUpdatedSubscription>>

export type QueueViewResult = SanitizedGql<ResultOf<typeof queueViewQuery>>

export async function fetchQueueView(
    id: string,
    currentEntityReferenceId: string | null
): Promise<QueueViewResult> {
    return gqlFetch(
        queueViewQuery,
        {
            id,
            currentEntityReferenceId,
            isAuthenticated: getAuth().isSignedIn(),
        },
        { isRetryableError: () => true }
    )
}
